import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tooltip, IconButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import AppButton from "../components/buttons/AppButton";
import StatusChip from "../components/common/StatusChip";

import useDebounce from "../hooks/useDebounce";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { usePermissions } from "../hooks/usePermissions";
import { getPurchaseOrders } from "../redux/actions/purchaseOrderActions";
import { getSuppliers } from "../redux/actions/supplierActions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { PurchaseOrderSummaryDto } from "../shared/dtos/purchaseOrder/PurchaseOrderSummaryDto";
import { PURCHASE_ORDER_STATUS_OPTIONS } from "../shared/data/enumOptions";
import { PurchaseOrderStatus } from "../shared/enums/PurchaseOrderStatus";
import { formatPlainNumber } from "../utils/appUtils";

const PurchaseOrdersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];

  const {
    items: orders,
    pagination,
    loadingFetchAll: loadingOrders,
    errorFetchAll: errorLoadingOrders,
  } = useAppSelector((state) => state.purchaseOrder);

  const { items: suppliers } = useAppSelector((state) => state.supplier);

  // Filter States
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);

  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const canRead = hasPermission("purchase_order:read_all");
  const canCreate = hasPermission("purchase_order:create");

  useEffect(() => {
    if (!suppliers.length) dispatch(getSuppliers({ page: 1, size: 100 }));
  }, [dispatch, suppliers.length]);

  const fetchOrders = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getPurchaseOrders({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
        supplierId: selectedSupplierId || undefined,
        status: (selectedStatus as PurchaseOrderStatus) || undefined,
        startDate,
        endDate,
      } as any),
    );
  }, [
    dispatch,
    pageConfig,
    debouncedSearch,
    selectedBranchId,
    selectedSupplierId,
    selectedStatus,
    startDate,
    endDate,
    canRead,
  ]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [
    debouncedSearch,
    selectedBranchId,
    selectedSupplierId,
    selectedStatus,
    startDate,
    endDate,
  ]);

  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ value: s.id, label: s.name })),
    [suppliers],
  );

  const columns: TableColumn<PurchaseOrderSummaryDto>[] = useMemo(
    () => [
      {
        id: "orderNumber",
        label: "PO #",
        minWidth: 120,
        format: (_, row) => (
          <b style={{ color: "#4cc9f0" }}>
            {row.orderNumber || row.id.substring(0, 8)}
          </b>
        ),
      },
      {
        id: "orderDate",
        label: "Order Date",
        minWidth: 120,
        format: (_, row) =>
          row.orderDate ? new Date(row.orderDate).toLocaleDateString() : "N/A",
      },
      { id: "supplierName", label: "Supplier", minWidth: 150 },
      {
        id: "status",
        label: "Status",
        minWidth: 120,
        format: (_, row) => <StatusChip status={row.status} />,
      },
      {
        id: "totalAmount",
        label: "CUR",
        minWidth: 60,
        align: "center",
        format: () => "KES",
      },
      {
        id: "totalAmount",
        label: "Total Cost",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.totalAmount)}
          </Typography>
        ),
      },
      {
        id: "branchName",
        label: "Campus",
        minWidth: 120,
        format: (_, row) => row.branchName || "N/A",
      },
    ],
    [],
  );

  const renderRowActions = (
    row: PurchaseOrderSummaryDto,
  ): React.JSX.Element => (
    <Tooltip title="View Purchase Order">
      <IconButton
        size="small"
        color="primary"
        onClick={() => navigateToTab(`/purchase-orders/${row.id}`)}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <PageContainer
      title="Purchase Orders"
      subtitle="Manage supplier orders, receive goods, and track supplier payments."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigateToTab("/purchase-orders/create")}
          >
            Create Purchase Order
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search PO # or Supplier..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={setSelectedBranchId}
        />

        <AppSelectFilter
          label="Supplier"
          value={selectedSupplierId}
          onChange={setSelectedSupplierId}
          options={supplierOptions}
          allLabel="All Suppliers"
          minWidth={150}
        />

        <AppSelectFilter
          label="Status"
          value={selectedStatus}
          onChange={setSelectedStatus}
          options={PURCHASE_ORDER_STATUS_OPTIONS}
          allLabel="All Statuses"
          minWidth={140}
        />

        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={orders}
        loading={loadingOrders}
        error={errorLoadingOrders}
        onRetry={fetchOrders}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig({ ...pageConfig, page: p })}
        onRowsPerPageChange={(l) =>
          setPageConfig({ ...pageConfig, limit: l, page: 1 })
        }
        rowAction={renderRowActions}
      />
    </PageContainer>
  );
};

export default PurchaseOrdersPage;
